/**
 * Generation store (Phase 3) — Zustand.
 *
 * Persistent state для /process/generation:
 * - datasetId, selectedColumns, tierKey, outputLanguage
 * - moduleKeys, temperature, maxTokens
 * - testRunLimit, isTestRun
 * - currentPipelineId (после старта)
 * - selectedContentType, contentConfigs (Block 2026-08-02 Generation foundation)
 *
 * Persist в localStorage (key: cs_generation_config).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { TierKey } from '@/hooks/useGeneration';
import {
  buildDefaultContentConfigs,
  DEFAULT_CONTENT_TYPE,
  CONTENT_TYPE_MAP,
} from '@/components/generation/contentTypes';

export type OutputFormat = 'plain' | 'html' | 'markdown';
export type SourceBasis = 'generated' | 'existing' | 'facts';

/** Per-content-type settings. Not every field is used by every variant,
 *  but one shape keeps the store/payload simple. Optional fields stay
 *  undefined until the user touches them. */
export interface ContentConfig {
  enabled: boolean;
  outputColumnName?: string;
  length?: number; // characters without spaces (spec § 16.5)
  style?: string;
  prompt?: string; // user context / main prompt
  additionalPrompt?: string;
  inputNotes?: string;
  negativePrompt?: string;
  temperature?: number;
  repetition?: number; // "тошнота" / repetition control
  academicity?: number; // 0..1 factual/academic precision
  outputFormat?: OutputFormat;
  // SEO-specific
  faqCount?: number;
  sourceBasis?: SourceBasis;
  // Custom-specific
  customName?: string;
  customPrompt?: string;
}

interface GenerationState {
  datasetId: string | null;
  selectedColumns: string[];
  tierKey: TierKey;
  outputLanguage: string;
  moduleKeys: string[];
  temperature: number;
  maxTokens: number;
  testRunLimit: number;
  isTestRun: boolean;
  currentPipelineId: string | null;
  // Generation foundation (2026-08-02)
  selectedContentType: string;
  contentConfigs: Record<string, ContentConfig>;

  setDatasetId: (id: string | null) => void;
  setSelectedColumns: (cols: string[]) => void;
  toggleColumn: (col: string) => void;
  setTierKey: (key: TierKey) => void;
  setOutputLanguage: (lang: string) => void;
  setModuleKeys: (keys: string[]) => void;
  toggleModule: (key: string) => void;
  setTemperature: (v: number) => void;
  setMaxTokens: (v: number) => void;
  setTestRunLimit: (n: number) => void;
  setIsTestRun: (v: boolean) => void;
  setCurrentPipelineId: (id: string | null) => void;
  setSelectedContentType: (key: string) => void;
  updateContentConfig: (key: string, patch: Partial<ContentConfig>) => void;
  toggleContentEnabled: (key: string) => void;
  reset: () => void;
}

const DEFAULTS = {
  datasetId: null as string | null,
  selectedColumns: [] as string[],
  tierKey: 'optimal' as TierKey,
  outputLanguage: 'Russian',
  moduleKeys: ['product_description'] as string[],
  temperature: 0.7,
  maxTokens: 2000,
  testRunLimit: 3,
  isTestRun: true,
  currentPipelineId: null as string | null,
  selectedContentType: DEFAULT_CONTENT_TYPE,
  contentConfigs: buildDefaultContentConfigs() as Record<string, ContentConfig>,
};

export const useGenerationStore = create<GenerationState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setDatasetId: (id) => set({ datasetId: id }),
      setSelectedColumns: (cols) => set({ selectedColumns: cols }),
      toggleColumn: (col) =>
        set((state) => ({
          selectedColumns: state.selectedColumns.includes(col)
            ? state.selectedColumns.filter((c) => c !== col)
            : [...state.selectedColumns, col],
        })),
      setTierKey: (key) => set({ tierKey: key }),
      setOutputLanguage: (lang) => set({ outputLanguage: lang }),
      setModuleKeys: (keys) => set({ moduleKeys: keys }),
      toggleModule: (key) =>
        set((state) => ({
          moduleKeys: state.moduleKeys.includes(key)
            ? state.moduleKeys.filter((k) => k !== key)
            : [...state.moduleKeys, key],
        })),
      setTemperature: (v) => set({ temperature: v }),
      setMaxTokens: (v) => set({ maxTokens: v }),
      setTestRunLimit: (n) =>
        set({ testRunLimit: Math.max(1, Math.min(20, Math.round(n))) }),
      setIsTestRun: (v) => set({ isTestRun: v }),
      setCurrentPipelineId: (id) => set({ currentPipelineId: id }),
      setSelectedContentType: (key) => set({ selectedContentType: key }),
      updateContentConfig: (key, patch) =>
        set((state) => {
          const def = CONTENT_TYPE_MAP[key]?.defaultConfig ?? {};
          const current = state.contentConfigs[key] ?? { ...def };
          return {
            contentConfigs: {
              ...state.contentConfigs,
              [key]: { ...current, ...patch },
            },
          };
        }),
      toggleContentEnabled: (key) =>
        set((state) => {
          const def = CONTENT_TYPE_MAP[key]?.defaultConfig ?? {};
          const current = state.contentConfigs[key] ?? { ...def };
          return {
            contentConfigs: {
              ...state.contentConfigs,
              [key]: { ...current, enabled: !current.enabled },
            },
          };
        }),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'cs_generation_config',
      partialize: (state) => ({
        tierKey: state.tierKey,
        outputLanguage: state.outputLanguage,
        moduleKeys: state.moduleKeys,
        temperature: state.temperature,
        maxTokens: state.maxTokens,
        testRunLimit: state.testRunLimit,
        isTestRun: state.isTestRun,
        selectedContentType: state.selectedContentType,
        contentConfigs: state.contentConfigs,
      }),
    },
  ),
);
